import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Search, ShoppingCart, Filter, FileText, Download, Eye, CheckCircle, XCircle, RotateCcw, Loader2 } from 'lucide-react';
import { exportDataset } from '../../lib/exportData';
import { toast } from 'sonner';
import FDVSnapshotView from '../sales/FDVSnapshotView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  // Ancien workflow (legacy)
  devis_signe: { label: 'Devis signe', class: 'bg-blue-100 text-blue-700 border-blue-200', order: 1 },
  facture_envoyee: { label: 'Facture envoyee', class: 'bg-purple-100 text-purple-700 border-purple-200', order: 2 },
  paiement_recu: { label: 'Paiement recu', class: 'bg-teal-100 text-teal-700 border-teal-200', order: 3 },
  securisation: { label: 'En securisation', class: 'bg-amber-100 text-amber-700 border-amber-200', order: 4 },
  // Nouveau workflow
  pending_validation: { label: 'En attente validation', class: 'bg-amber-100 text-amber-700 border-amber-200', order: 0 },
  valide_en_attente: { label: 'Valide - En attente 30j', class: 'bg-blue-100 text-blue-700 border-blue-200', order: 1 },
  commission_eligible: { label: 'Commission eligible', class: 'bg-emerald-100 text-emerald-700 border-emerald-200', order: 2 },
  facture_soumise: { label: 'Facture soumise', class: 'bg-purple-100 text-purple-700 border-purple-200', order: 3 },
  en_attente_paiement: { label: 'En attente paiement', class: 'bg-cyan-100 text-cyan-700 border-cyan-200', order: 4 },
  paye: { label: 'Paye', class: 'bg-green-100 text-green-800 border-green-200', order: 5 },
  rejete: { label: 'Rejete', class: 'bg-red-100 text-red-700 border-red-200', order: -1 },
};

export const AdminSales = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSale, setSelectedSale] = useState(null);
  const [saleDocuments, setSaleDocuments] = useState([]);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [rejectComment, setRejectComment] = useState('');
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchSales();
  }, []);

  const fetchSales = async () => {
    try {
      const response = await fetch(`${API}/sales`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSales(data);
      }
    } catch (error) {
      console.error('Failed to fetch sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSaleDocuments = async (saleId) => {
    try {
      const response = await fetch(`${API}/sales/${saleId}/documents`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSaleDocuments(data);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const downloadDocument = async (saleId, docId, filename) => {
    setDownloading(docId);
    try {
      const response = await fetch(`${API}/sales/${saleId}/documents/${docId}/download`, {
        credentials: 'include',
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Document telecharge');
      } else {
        toast.error('Erreur lors du telechargement');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du telechargement');
    } finally {
      setDownloading(null);
    }
  };

  const openSaleDetail = async (sale) => {
    setSelectedSale(sale);
    await fetchSaleDocuments(sale.sale_id);
    setIsDetailOpen(true);
  };

  const updateSaleStatus = async (saleId, newStatus, comment = null) => {
    setActionLoading(true);
    try {
      const body = { status: newStatus };
      if (comment) body.rejection_comment = comment;
      
      const response = await fetch(`${API}/sales/${saleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      
      if (response.ok) {
        setSales(prev => prev.map(s => s.sale_id === saleId ? { ...s, status: newStatus } : s));
        if (selectedSale?.sale_id === saleId) {
          setSelectedSale(prev => ({ ...prev, status: newStatus }));
        }
        toast.success('Statut mis a jour');
        setIsRejectOpen(false);
        setRejectComment('');
      }
    } catch (error) {
      console.error('Failed to update sale:', error);
      toast.error('Erreur lors de la mise a jour');
    } finally {
      setActionLoading(false);
    }
  };

  const validateSale = async (saleId) => {
    const sale = sales.find(s => s.sale_id === saleId);
    if (!sale) return;
    
    setActionLoading(true);
    try {
      let endpoint = '';
      let method = 'PUT';
      
      // Determine the right endpoint based on current status
      if (['pending_validation', 'devis_signe', 'facture_envoyee', 'paiement_recu'].includes(sale.status)) {
        // First validation: move to valide_en_attente
        endpoint = `${API}/sales/${saleId}/validate`;
      } else if (['valide_en_attente', 'securisation'].includes(sale.status)) {
        // Make eligible manually (before 30 days)
        endpoint = `${API}/sales/${saleId}/make-eligible`;
      } else if (sale.status === 'en_attente_paiement') {
        // Mark as paid - use the generic update endpoint
        await updateSaleStatus(saleId, 'paye');
        return;
      } else {
        toast.info('Cette vente ne peut pas etre modifiee');
        return;
      }
      
      const response = await fetch(endpoint, {
        method,
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Statut mis a jour');
        fetchSales();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la mise a jour');
      }
    } catch (error) {
      console.error('Failed to update sale:', error);
      toast.error('Erreur lors de la mise a jour');
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectDialog = (sale) => {
    setSelectedSale(sale);
    setIsRejectOpen(true);
  };

  const handleReject = async () => {
    if (!rejectComment.trim()) {
      toast.error('Veuillez indiquer le motif du rejet');
      return;
    }
    await updateSaleStatus(selectedSale.sale_id, 'rejete', rejectComment);
  };

  const filteredSales = sales.filter(sale => {
    const matchesSearch = sale.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sale.commercial_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sale.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalAmount = filteredSales.reduce((sum, sale) => sum + (sale.amount || 0), 0);

  const getNextAction = (status) => {
    const actions = {
      pending_validation: 'Valider la vente',
      valide_en_attente: 'Rendre eligible (avant 30j)',
      commission_eligible: 'En attente facture AC',
      facture_soumise: 'Valider la facture',
      en_attente_paiement: 'Marquer comme paye',
      // Legacy
      devis_signe: 'Valider la vente',
      facture_envoyee: 'Valider la vente',
      paiement_recu: 'Valider la vente',
      securisation: 'Rendre eligible',
    };
    return actions[status] || null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="admin-sales-title">
            Toutes les ventes
          </h1>
          <p className="text-slate-500 mt-1">Gerez et validez les ventes de l'equipe commerciale</p>
        </div>
        <Button variant="outline" onClick={() => exportDataset('sales', 'ventes')} data-testid="export-sales-btn">
          <Download className="w-4 h-4 mr-2" /> Exporter (CSV)
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-indigo-100">
              <ShoppingCart className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total ventes</p>
              <p className="text-xl font-bold text-slate-900">{sales.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <span className="text-emerald-600 font-bold text-lg">€</span>
            </div>
            <div>
              <p className="text-sm text-slate-500">CA Total</p>
              <p className="text-xl font-bold text-slate-900">{totalAmount.toLocaleString('fr-FR')} €</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <Filter className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">A valider</p>
              <p className="text-xl font-bold text-amber-600">
                {sales.filter(s => !['paye', 'rejete'].includes(s.status)).length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-red-100">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Rejetes</p>
              <p className="text-xl font-bold text-red-600">
                {sales.filter(s => s.status === 'rejete').length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher client ou commercial..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-admin-sales-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[200px]" data-testid="admin-status-filter">
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(statusConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Sales table */}
      <Card className="border-0 shadow-sm overflow-hidden" data-testid="admin-sales-table-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-900">
                <TableHead className="font-semibold">Commercial</TableHead>
                <TableHead className="font-semibold">Client</TableHead>
                <TableHead className="font-semibold">Offre</TableHead>
                <TableHead className="font-semibold">Montant</TableHead>
                <TableHead className="font-semibold">Statut</TableHead>
                <TableHead className="font-semibold">Documents</TableHead>
                <TableHead className="font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : filteredSales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    Aucune vente trouvee
                  </TableCell>
                </TableRow>
              ) : (
                filteredSales.map((sale, index) => (
                  <TableRow 
                    key={sale.sale_id}
                    className={`table-row-hover animate-fade-in ${sale.status === 'rejete' ? 'bg-red-50' : ''}`}
                    style={{ animationDelay: `${index * 0.02}s` }}
                    data-testid={`admin-sale-row-${sale.sale_id}`}
                  >
                    <TableCell className="font-medium">{sale.commercial_name || '-'}</TableCell>
                    <TableCell>{sale.client_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{sale.offer}</Badge>
                    </TableCell>
                    <TableCell className="font-semibold">{sale.amount?.toLocaleString('fr-FR')} €</TableCell>
                    <TableCell>
                      <Badge className={`${statusConfig[sale.status]?.class || 'bg-slate-100'} border`}>
                        {statusConfig[sale.status]?.label || sale.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openSaleDetail(sale)}
                        className="text-indigo-600"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Voir ({sale.documents?.length || 0})
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {sale.status !== 'paye' && sale.status !== 'rejete' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => validateSale(sale.sale_id)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              title={getNextAction(sale.status)}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRejectDialog(sale)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Rejeter / Renvoyer"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {sale.status === 'rejete' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateSaleStatus(sale.sale_id, 'devis_signe')}
                            className="text-amber-600"
                            title="Remettre en cours"
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Reprendre
                          </Button>
                        )}
                        {sale.status === 'paye' && (
                          <span className="text-emerald-600 font-medium text-sm">Complete</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Sale Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Details de la vente</DialogTitle>
            <DialogDescription>
              {selectedSale?.client_name} - {selectedSale?.commercial_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Offre</p>
                <p className="font-medium">{selectedSale?.offer}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Montant</p>
                <p className="font-bold text-emerald-600">{selectedSale?.amount?.toLocaleString('fr-FR')} €</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Statut</p>
                <Badge className={`${statusConfig[selectedSale?.status]?.class} border mt-1`}>
                  {statusConfig[selectedSale?.status]?.label}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-slate-500">Date</p>
                <p className="font-medium">
                  {selectedSale?.created_at ? new Date(selectedSale.created_at).toLocaleDateString('fr-FR') : '-'}
                </p>
              </div>
            </div>

            {/* Dynamic FDV snapshot rendering */}
            {selectedSale?.template_snapshot && (
              <FDVSnapshotView
                snapshot={selectedSale.template_snapshot}
                formValues={selectedSale.form_values || {}}
                documents={saleDocuments}
                onDownload={(docId, filename) => downloadDocument(selectedSale.sale_id, docId, filename)}
                downloadingId={downloading}
              />
            )}

            {/* Legacy fallback (sales without snapshot) */}
            {!selectedSale?.template_snapshot && selectedSale?.declaration_data && (
              <>
                {/* Partner / Apporteur */}
                {selectedSale.declaration_data.partner && (
                  <div className="border-t pt-4">
                    <p className="font-semibold text-indigo-600 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Apporteur / Partenaire
                    </p>
                    <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                      <div>
                        <p className="text-sm text-slate-500">Prenom</p>
                        <p className="font-medium">{selectedSale.declaration_data.partner.firstname || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Nom</p>
                        <p className="font-medium">{selectedSale.declaration_data.partner.lastname || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Societe</p>
                        <p className="font-medium">{selectedSale.declaration_data.partner.company || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Email</p>
                        <p className="font-medium">{selectedSale.declaration_data.partner.email || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Telephone</p>
                        <p className="font-medium">{selectedSale.declaration_data.partner.phone || '-'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Establishment Info */}
                {selectedSale.declaration_data.establishment && (
                  <div className="border-t pt-4">
                    <p className="font-semibold text-indigo-600 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Etablissement
                    </p>
                    <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                      <div>
                        <p className="text-sm text-slate-500">Nom etablissement</p>
                        <p className="font-medium">{selectedSale.declaration_data.establishment.name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Type</p>
                        <p className="font-medium">{selectedSale.declaration_data.establishment.type || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Region academique</p>
                        <p className="font-medium">{selectedSale.declaration_data.establishment.region || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Telephone</p>
                        <p className="font-medium">{selectedSale.declaration_data.establishment.phone || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Email</p>
                        <p className="font-medium">{selectedSale.declaration_data.establishment.email || '-'}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm text-slate-500">Adresse</p>
                        <p className="font-medium">
                          {selectedSale.declaration_data.establishment.address?.street || '-'}
                          {selectedSale.declaration_data.establishment.address?.complement && `, ${selectedSale.declaration_data.establishment.address.complement}`}
                          {' '}
                          {selectedSale.declaration_data.establishment.address?.postal} {selectedSale.declaration_data.establishment.address?.city}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Contact Info */}
                {selectedSale.declaration_data.contact && (
                  <div className="border-t pt-4">
                    <p className="font-semibold text-indigo-600 mb-3">Contact client</p>
                    <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                      <div>
                        <p className="text-sm text-slate-500">Nom</p>
                        <p className="font-medium">{selectedSale.declaration_data.contact.name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Fonction</p>
                        <p className="font-medium">{selectedSale.declaration_data.contact.function || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Email</p>
                        <p className="font-medium">{selectedSale.declaration_data.contact.email || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Telephone</p>
                        <p className="font-medium">{selectedSale.declaration_data.contact.phone || '-'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Offer Details */}
                {selectedSale.declaration_data.offer && (
                  <div className="border-t pt-4">
                    <p className="font-semibold text-indigo-600 mb-3">Details de l'offre</p>
                    <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                      <div>
                        <p className="text-sm text-slate-500">Offre</p>
                        <p className="font-medium">{selectedSale.declaration_data.offer.name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Duree</p>
                        <p className="font-medium">{selectedSale.declaration_data.offer.duration || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Prix TTC</p>
                        <p className="font-medium">{selectedSale.declaration_data.offer.price_ttc || '-'} €</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Remise</p>
                        <p className="font-medium">
                          {selectedSale.declaration_data.offer.discount_percent ? `${selectedSale.declaration_data.offer.discount_percent}%` : '-'}
                          {selectedSale.declaration_data.offer.discount_reason && ` (${selectedSale.declaration_data.offer.discount_reason})`}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total TTC final</p>
                        <p className="font-bold text-emerald-600">{selectedSale.declaration_data.offer.total_ttc || selectedSale.amount} €</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Info */}
                {selectedSale.declaration_data.payment && (
                  <div className="border-t pt-4">
                    <p className="font-semibold text-indigo-600 mb-3">Paiement</p>
                    <div className="grid grid-cols-3 gap-4 bg-slate-50 p-4 rounded-lg">
                      <div>
                        <p className="text-sm text-slate-500">Mode</p>
                        <p className="font-medium">{selectedSale.declaration_data.payment.mode || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Type</p>
                        <p className="font-medium">{selectedSale.declaration_data.payment.type || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Date</p>
                        <p className="font-medium">{selectedSale.declaration_data.payment.date || '-'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Account Info */}
                {selectedSale.declaration_data.account && (
                  <div className="border-t pt-4">
                    <p className="font-semibold text-indigo-600 mb-3">Compte client</p>
                    <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                      <div>
                        <p className="text-sm text-slate-500">Email admin</p>
                        <p className="font-medium">{selectedSale.declaration_data.account.admin_email || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Referent</p>
                        <p className="font-medium">{selectedSale.declaration_data.account.referent_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Nombre utilisateurs</p>
                        <p className="font-medium">{selectedSale.declaration_data.account.users_count || '-'}</p>
                      </div>
                      {selectedSale.declaration_data.account.technical_comment && (
                        <div className="col-span-2">
                          <p className="text-sm text-slate-500">Commentaire technique</p>
                          <p className="font-medium">{selectedSale.declaration_data.account.technical_comment}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Qualification */}
                {selectedSale.declaration_data.qualification && (
                  <div className="border-t pt-4">
                    <p className="font-semibold text-indigo-600 mb-3">Qualification</p>
                    <div className="grid grid-cols-3 gap-4 bg-slate-50 p-4 rounded-lg">
                      <div>
                        <p className="text-sm text-slate-500">Origine du lead</p>
                        <p className="font-medium">{selectedSale.declaration_data.qualification.lead_origin || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Niveau urgence</p>
                        <p className="font-medium">{selectedSale.declaration_data.qualification.urgency_level || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Statut vente</p>
                        <p className="font-medium">{selectedSale.declaration_data.qualification.sale_status || '-'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Documents (legacy fallback only; snapshot view includes documents) */}
            {!selectedSale?.template_snapshot && (
            <div className="border-t pt-4">
              <p className="font-semibold mb-3">Documents attaches ({saleDocuments.length})</p>
              {saleDocuments.length === 0 ? (
                <p className="text-slate-500 text-sm">Aucun document</p>
              ) : (
                <div className="space-y-2">
                  {saleDocuments.map((doc) => (
                    <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-slate-500" />
                        <div>
                          <p className="font-medium text-sm">{doc.filename}</p>
                          <p className="text-xs text-slate-500">{doc.type}</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadDocument(selectedSale.sale_id, doc.doc_id, doc.filename)}
                        disabled={downloading === doc.doc_id}
                      >
                        {downloading === doc.doc_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-1" />
                            Telecharger
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
          </div>
          <DialogFooter>
            {selectedSale?.status !== 'paye' && selectedSale?.status !== 'rejete' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => openRejectDialog(selectedSale)}
                  className="text-red-600"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejeter
                </Button>
                <Button
                  onClick={() => {
                    validateSale(selectedSale.sale_id);
                    setIsDetailOpen(false);
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {getNextAction(selectedSale?.status) || 'Valider'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={isRejectOpen} onOpenChange={setIsRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeter / Renvoyer la vente</DialogTitle>
            <DialogDescription>
              Indiquez le motif du rejet pour que le commercial puisse corriger
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="Motif du rejet (ex: document manquant, montant incorrect...)"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRejectOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleReject}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Rejeter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
